import { DropPanel } from '../components/DropPanel.js';

const FILTER_WIDGET_INITIALIZED_ATTRIBUTE = 'data-catalog-filter-initialized';
const FILTER_SEARCH_INITIALIZED_ATTRIBUTE = 'data-catalog-search-initialized';
const FILTER_MOBILE_ACTIONS_ATTRIBUTE = 'data-catalog-mobile-actions-initialized';
const MOBILE_FILTER_MAX_WIDTH = 680.98;

let activeCatalogFilterId = '';
let dropPanelInstance = null;
let mobileFilterScrollTop = 0;

function isCatalogFilterDropdown(wrapper) {
    return Boolean(wrapper && wrapper.closest('.catalog__filter'));
}

function isMobileFilterViewport() {
    return window.innerWidth <= MOBILE_FILTER_MAX_WIDTH;
}

function getMobileFilterScrollContainer() {
    return document.querySelector('.catalog__filter .catalog__filter-filter');
}

function rememberMobileFilterScrollPosition() {
    if (!isMobileFilterViewport()) {
        mobileFilterScrollTop = 0;
        return;
    }

    const scrollContainer = getMobileFilterScrollContainer();

    mobileFilterScrollTop = scrollContainer ? scrollContainer.scrollTop : 0;
}

function restoreMobileFilterScrollPosition() {
    if (!isMobileFilterViewport() || mobileFilterScrollTop <= 0) {
        return;
    }

    const scrollContainer = getMobileFilterScrollContainer();

    if (!scrollContainer) {
        return;
    }

    scrollContainer.scrollTop = mobileFilterScrollTop;
}

function rememberCatalogFilterState(wrapper) {
    if (!isCatalogFilterDropdown(wrapper)) {
        return;
    }

    activeCatalogFilterId = wrapper.dataset.fid || '';
}

function clearCatalogFilterState(wrapper) {
    if (!isCatalogFilterDropdown(wrapper)) {
        return;
    }

    const closedFilterId = wrapper.dataset.fid || '';

    if (closedFilterId && closedFilterId === activeCatalogFilterId) {
        activeCatalogFilterId = '';
    }
}

function restoreCatalogFilterState() {
    if (!activeCatalogFilterId || !dropPanelInstance) {
        restoreMobileFilterScrollPosition();
        return;
    }

    const filterWrapper = document.querySelector(`.catalog__filter [data-dropdown="wrapper"][data-fid="${activeCatalogFilterId}"]`);

    if (!filterWrapper) {
        activeCatalogFilterId = '';
        restoreMobileFilterScrollPosition();
        return;
    }

    dropPanelInstance.open(filterWrapper);
    window.requestAnimationFrame(() => {
        restoreMobileFilterScrollPosition();
    });
}

function initDropPanels() {
    if (dropPanelInstance) {
        dropPanelInstance.destroy();
    }

    dropPanelInstance = new DropPanel({
        onOpen  : (wrapper) => rememberCatalogFilterState(wrapper),
        onClose : (wrapper) => clearCatalogFilterState(wrapper)
    });

    dropPanelInstance.init();
}

function getSearchField(filterRoot) {
    const searchBlock = document.querySelector('[data-filter-search-container] [data-filter-search-external]');

    if (searchBlock) {
        return searchBlock.querySelector('.catalog__search-input');
    }

    return filterRoot.querySelector('.wpc-search-field');
}

function buildExternalSearchBlock(searchBlock) {
    const externalSearchBlock = searchBlock.cloneNode(true);
    const externalForm = externalSearchBlock.querySelector('form');
    const originalInput = searchBlock.querySelector('.wpc-search-field');
    const externalInput = externalSearchBlock.querySelector('.wpc-search-field');
    const originalLabel = searchBlock.querySelector('label');
    const externalLabel = externalSearchBlock.querySelector('label');

    externalSearchBlock.setAttribute('data-filter-search-external', 'true');
    externalSearchBlock.removeAttribute('data-filter-search');
    externalSearchBlock.hidden = false;
    externalSearchBlock.classList.remove('catalog__search--original');
    externalSearchBlock.classList.add('catalog__search--external');

    if (externalForm) {
        externalForm.classList.remove('wpc-filter-search-form');
        externalForm.classList.add('catalog__search-form');
    }

    if (externalInput) {
        externalInput.classList.remove('wpc-search-field');
        externalInput.classList.add('catalog__search-input');
    }

    if (originalInput && externalInput) {
        const externalInputId = `${originalInput.id || 'catalog-search'}-external`;

        externalInput.id = externalInputId;
        externalInput.value = originalInput.value;
    }

    if (externalLabel && originalLabel && externalInput) {
        externalLabel.setAttribute('for', externalInput.id);
    }

    return externalSearchBlock;
}

function syncSearchValue(sourceField, targetField) {
    if (!sourceField || !targetField) {
        return;
    }

    targetField.value = sourceField.value;
}

function bindExternalSearchEvents({ filterRoot, originalSearchBlock, externalSearchBlock }) {
    if (!filterRoot || !originalSearchBlock || !externalSearchBlock) {
        return;
    }

    if (externalSearchBlock.hasAttribute(FILTER_SEARCH_INITIALIZED_ATTRIBUTE)) {
        return;
    }

    const originalInput = originalSearchBlock.querySelector('.wpc-search-field');
    const originalForm = originalSearchBlock.querySelector('.wpc-filter-search-form');
    const originalClearLink = originalSearchBlock.querySelector('.wpc-search-clear-icon');
    const externalInput = externalSearchBlock.querySelector('.catalog__search-input');
    const externalForm = externalSearchBlock.querySelector('.catalog__search-form');
    const externalClearLink = externalSearchBlock.querySelector('.wpc-search-clear-icon');

    if (externalInput && originalInput) {
        externalInput.addEventListener('input', () => {
            syncSearchValue(externalInput, originalInput);
            originalInput.dispatchEvent(new Event('input', { bubbles : true }));
            updateSelectedFiltersCount(filterRoot);
        });

        externalInput.addEventListener('change', () => {
            syncSearchValue(externalInput, originalInput);
            originalInput.dispatchEvent(new Event('change', { bubbles : true }));
            updateSelectedFiltersCount(filterRoot);
        });
    }

    if (externalForm && originalForm && originalInput && externalInput) {
        externalForm.addEventListener('submit', (event) => {
            event.preventDefault();
            syncSearchValue(externalInput, originalInput);
            originalForm.dispatchEvent(new Event('submit', { bubbles : true, cancelable : true }));
        });
    }

    if (externalClearLink && originalInput) {
        externalClearLink.addEventListener('click', (event) => {
            event.preventDefault();

            if (externalInput) {
                externalInput.value = '';
            }

            originalInput.value = '';
            originalInput.dispatchEvent(new Event('input', { bubbles : true }));
            originalInput.dispatchEvent(new Event('change', { bubbles : true }));

            if (originalClearLink) {
                window.location.href = originalClearLink.href;
                return;
            }

            if (originalForm) {
                originalForm.dispatchEvent(new Event('submit', { bubbles : true, cancelable : true }));
            }
        });
    }

    externalSearchBlock.setAttribute(FILTER_SEARCH_INITIALIZED_ATTRIBUTE, 'true');
}

function getFilterIconMarkup() {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2 5.33334L10 5.33334M10 5.33334C10 6.43791 10.8954 7.33334 12 7.33334C13.1046 7.33334 14 6.43791 14 5.33334C14 4.22877 13.1046 3.33334 12 3.33334C10.8954 3.33334 10 4.22877 10 5.33334ZM6 10.6667L14 10.6667M6 10.6667C6 11.7712 5.10457 12.6667 4 12.6667C2.89543 12.6667 2 11.7712 2 10.6667C2 9.56211 2.89543 8.66668 4 8.66668C5.10457 8.66668 6 9.56211 6 10.6667Z" stroke="#717680" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"></path></svg>';
}

function getCloseIconMarkup() {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M18 6L6 18M6 6L18 18" stroke="#A4A7AE" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>';
}

function buildMobileTopBlock() {
    const mobileTop = document.createElement('div');

    mobileTop.className = 'catalog__mobile-top';
    mobileTop.setAttribute('data-filter-mobile-top', 'true');
    mobileTop.innerHTML = `${
        '<div class="catalog__mobile-btn">'
    }${getFilterIconMarkup()}<span>Filter</span><span class="catalog__count" data-filter-count hidden></span></div><div class="catalog__mobile-close" data-dropdown="button">${getCloseIconMarkup()}</div>`;

    return mobileTop;
}

function buildMobileButtonsBlock() {
    const mobileButtons = document.createElement('div');

    mobileButtons.className = 'catalog__mobile-buttons';
    mobileButtons.setAttribute('data-filter-mobile-buttons', 'true');
    mobileButtons.innerHTML = '<a class="b-btn b-btn--secondary-grey b-btn--md b-btn--full-size wpc-filters-reset-button" href="#" data-filter-action="clear">Clear Filters</a><button class="b-btn b-btn--primary b-btn--md b-btn--full-size" type="button" data-filter-action="apply">Apply</button>';

    return mobileButtons;
}

function getFilterQueryParameterNames(filterRoot) {
    const formControls = filterRoot.querySelectorAll('input[name], select[name], textarea[name]');
    const parameterNames = new Set();

    formControls.forEach((formControl) => {
        const parameterName = formControl.getAttribute('name');

        if (!parameterName) {
            return;
        }

        parameterNames.add(parameterName);
    });

    return Array.from(parameterNames);
}

function buildCatalogResetUrl(filterRoot) {
    try {
        const url = new URL(window.location.href);
        const parameterNames = getFilterQueryParameterNames(filterRoot);

        parameterNames.forEach((parameterName) => {
            url.searchParams.delete(parameterName);
        });

        url.searchParams.delete('paged');
        url.searchParams.delete('product-page');

        return url.toString();
    } catch (error) {
        return window.location.href;
    }
}

function closeCatalogFilter(filterRoot) {
    const filterWidget = filterRoot.closest('.catalog__filter');

    if (!filterWidget) {
        return;
    }

    if (dropPanelInstance) {
        dropPanelInstance.close(filterWidget);
        return;
    }

    filterWidget.classList.remove('is-open');
}

function bindMobileButtonsEvents(filterRoot) {
    const mobileButtons = filterRoot.querySelector('[data-filter-mobile-buttons]');

    if (!mobileButtons || mobileButtons.hasAttribute(FILTER_MOBILE_ACTIONS_ATTRIBUTE)) {
        return;
    }

    const nativeResetButton = filterRoot.querySelector('a.wpc-filters-reset-button:not([data-filter-action="clear"])');
    const clearButton = mobileButtons.querySelector('[data-filter-action="clear"]');
    const applyButton = mobileButtons.querySelector('[data-filter-action="apply"]');

    if (clearButton) {
        clearButton.setAttribute('href', buildCatalogResetUrl(filterRoot));

        if (nativeResetButton) {
            nativeResetButton.setAttribute('href', clearButton.getAttribute('href') || window.location.href);
        }

        if (nativeResetButton && nativeResetButton.classList.contains('on-hold')) {
            clearButton.classList.add('on-hold');
            clearButton.setAttribute('aria-disabled', 'true');
        } else {
            clearButton.classList.remove('on-hold');
            clearButton.removeAttribute('aria-disabled');
        }
    }

    if (clearButton) {
        clearButton.addEventListener('click', () => {
            closeCatalogFilter(filterRoot);
        });
    }

    if (applyButton) {
        applyButton.addEventListener('click', () => {
            closeCatalogFilter(filterRoot);
        });
    }

    mobileButtons.setAttribute(FILTER_MOBILE_ACTIONS_ATTRIBUTE, 'true');
}

function getCatalogFoundPostsCount(filterRoot) {
    const filterWidget = filterRoot.closest('.catalog__filter');
    const foundPostsElement = filterWidget
        ? filterWidget.querySelector('.wpc-filters-found-posts')
        : filterRoot.querySelector('.wpc-filters-found-posts');

    if (foundPostsElement) {
        const normalizedValue = foundPostsElement.textContent.replace(/\s+/g, '').trim();
        const foundPostsCount = Number.parseInt(normalizedValue, 10);

        if (!Number.isNaN(foundPostsCount)) {
            return foundPostsCount;
        }
    }

    const productsContainer = document.querySelector('#js-filter-product');

    if (!productsContainer) {
        return 0;
    }

    if (productsContainer.querySelector('.product-empty')) {
        return 0;
    }

    return productsContainer.querySelectorAll('.product').length;
}

function hasActiveCatalogFiltering(filterRoot) {
    const parameterNames = getFilterQueryParameterNames(filterRoot);

    try {
        const url = new URL(window.location.href);

        return parameterNames.some((parameterName) => url.searchParams.has(parameterName));
    } catch (error) {
        return false;
    }
}

function updateSelectedFiltersCount(filterRoot) {
    const selectedFiltersCount = getCatalogFoundPostsCount(filterRoot);
    const hasActiveFiltering = hasActiveCatalogFiltering(filterRoot);
    const filterWidget = filterRoot.closest('.catalog__filter');
    const counterElements = filterWidget
        ? filterWidget.querySelectorAll('[data-filter-count]')
        : filterRoot.querySelectorAll('[data-filter-count]');

    counterElements.forEach((counterElement) => {
        counterElement.textContent = String(selectedFiltersCount);
        counterElement.hidden = !hasActiveFiltering;
    });
}

function decorateFilterWidget(filterRoot) {
    const widgetContent = filterRoot.querySelector('.wpc-filters-widget-content');
    const scrollContainer = filterRoot.querySelector('.wpc-filters-scroll-container');
    const existingMobileTop = scrollContainer ? scrollContainer.querySelector('[data-filter-mobile-top]') : null;
    const existingMobileButtons = filterRoot.querySelector('[data-filter-mobile-buttons]');
    const searchBlock = filterRoot.querySelector('.catalog__search');
    const searchContainer = document.querySelector('[data-filter-search-container]');
    const existingSearchBlock = searchContainer ? searchContainer.querySelector('[data-filter-search-external]') : null;

    if (widgetContent) {
        widgetContent.classList.add('catalog__filter-wrap');
    }

    if (scrollContainer) {
        scrollContainer.classList.add('catalog__filter-filter');

        if (existingMobileTop) {
            existingMobileTop.remove();
        }

        scrollContainer.prepend(buildMobileTopBlock());

        if (existingMobileButtons) {
            existingMobileButtons.remove();
        }

        scrollContainer.insertAdjacentElement('afterend', buildMobileButtonsBlock());
    }

    if (searchBlock && searchContainer) {
        const externalSearchBlock = buildExternalSearchBlock(searchBlock);

        if (existingSearchBlock) {
            searchContainer.querySelectorAll('[data-filter-search-external]').forEach((searchNode) => {
                searchNode.remove();
            });
        }

        searchContainer.append(externalSearchBlock);
        bindExternalSearchEvents({
            filterRoot,
            originalSearchBlock : searchBlock,
            externalSearchBlock
        });

        searchBlock.classList.add('catalog__search--original');
        searchBlock.hidden = true;
    }

    bindMobileButtonsEvents(filterRoot);
}

function bindFilterCounterEvents(filterRoot) {
    if (filterRoot.hasAttribute(FILTER_WIDGET_INITIALIZED_ATTRIBUTE)) {
        return;
    }

    const formControls = filterRoot.querySelectorAll('input, select');
    const searchField = getSearchField(filterRoot);

    formControls.forEach((formControl) => {
        formControl.addEventListener('change', () => updateSelectedFiltersCount(filterRoot));
        formControl.addEventListener('input', () => updateSelectedFiltersCount(filterRoot));
    });

    if (searchField) {
        searchField.addEventListener('change', () => updateSelectedFiltersCount(filterRoot));
        searchField.addEventListener('input', () => updateSelectedFiltersCount(filterRoot));
    }

    filterRoot.setAttribute(FILTER_WIDGET_INITIALIZED_ATTRIBUTE, 'true');
}

function initCatalogFilters() {
    const filterRoots = document.querySelectorAll('.catalog__filter .wpc-filters-main-wrap');
    const filterBlock = document.querySelector('.catalog__block-filter');

    if (!filterRoots.length) {
        return;
    }

    filterRoots.forEach((filterRoot) => {
        decorateFilterWidget(filterRoot);
        bindFilterCounterEvents(filterRoot);
        updateSelectedFiltersCount(filterRoot);
    });

    restoreCatalogFilterState();

    if (filterBlock) {
        filterBlock.classList.add('is-ready');
    }
}

function initCatalogFiltersOnAjax() {
    if (!window.jQuery) {
        return;
    }

    window.jQuery(document).on('change', '.catalog__filter .wpc-filters-main-wrap input, .catalog__filter .wpc-filters-main-wrap select', () => {
        rememberMobileFilterScrollPosition();
    });

    window.jQuery(document).on('submit', '.catalog__filter .wpc-filters-main-wrap .wpc-filter-search-form', () => {
        rememberMobileFilterScrollPosition();
    });

    window.jQuery(document).on('click', '.catalog__filter .wpc-filters-main-wrap a.wpc-filters-reset-button, .catalog__filter .wpc-filters-main-wrap a.wpc-search-clear-icon', () => {
        rememberMobileFilterScrollPosition();
    });

    window.jQuery(document).on('ready', () => {
        window.requestAnimationFrame(() => {
            initDropPanels();
            initCatalogFilters();
        });
    });
}

export function initCatalogFiltersFeature() {
    initDropPanels();
    initCatalogFilters();
    initCatalogFiltersOnAjax();
}
